import { Container, useBreakpointValue } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import {
  IOfferingBaseScopeEnum,
  IOfferingScopeEnum,
} from '@r-client/data/graphql-types';
import { EuOfferings, UsOfferings } from '@r-client/republic/feature/global';
import { TRegionValidIds } from '@r-client/shared/feature/region';
import { useAppConfig } from '@r-client/shared/util/core';

import { colors, containerSize } from '../../config/styles';
import { MotionBox } from '../motion-box/motion-box';

export interface IOpportunitiesProps {
  region: TRegionValidIds;
}

/**
 * Lands on the growth-stage ventures section of the new opportunities page,
 * replacing `/companies?sorting=most-traction-last-week` — that sort has no
 * equivalent in the new page's menu, so this falls back to its default
 * (trending), which is the absence of a `sort` param.
 */
const VIEW_ALL_HREF = '/investment-opportunities#growth-stage-ventures';

export const Opportunities = observer(function Opportunities({
  region,
}: IOpportunitiesProps) {
  const { republicEuropeBaseUrl } = useAppConfig();
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isGlobal = region === 'global';
  const usRegionOfferingsLimit = isGlobal || isMobile ? 3 : 6;

  return (
    <Container
      as="section"
      id="opportunities"
      maxW={containerSize}
      pt={{ base: '8', sm: '24' }}
      pb={{ base: '16', sm: '20' }}
    >
      <MotionBox>
        <UsOfferings
          first={usRegionOfferingsLimit}
          scopeFilter={{
            scopeName: IOfferingScopeEnum.MostTraction,
            mostTractionPeriodDays: 30,
          }}
          scopeBaseFilter={[
            IOfferingBaseScopeEnum.Published,
            IOfferingBaseScopeEnum.ShowOnHomepage,
          ]}
          viewAllButtonHref={VIEW_ALL_HREF}
          heading={isGlobal ? 'US opportunities' : 'Featured opportunities'}
          headingColor={colors.darkNavyTypography}
        />
      </MotionBox>

      {isGlobal && (
        <MotionBox mt="24">
          <EuOfferings
            first={3}
            sort="most_recent_desc"
            viewAllButtonHref={`${republicEuropeBaseUrl}/invest/raising-now?sort=trending_desc&sort_collection=campaigns`}
            heading="UK and EU opportunities"
            headingColor={colors.darkNavyTypography}
          />
        </MotionBox>
      )}
    </Container>
  );
});
